import angular from 'angular'
import React from 'react'
import { createRoot } from 'react-dom/client'
import ReactDOM from 'react-dom'
import { Performance } from '../../analytics'
import {
  ApplicationsGrid,
  ApplicationsGridProvider
} from '../../components/applications-grid'
import { AppObserveTab } from '../../components/application-observe'
import { AppConfigPage } from '../../components/app-config-page'

import { AppPolicyTable } from '../../components/application-policy'
import { RepositoriesGrid } from '../../components/repositories-grid'
import { IntegrationsPanelComponent } from '../../components/org-settings-integration-panel'
import { AttackEventsPage } from '../../components/attack-events-page'
import { AttackEventsGridProvider } from '../../components/attack-events-grid'
import {
  RouteCoverageGrid,
  RouteCoverageGridProvider
} from '../../components/route-coverage-grid/grid'
import { RouteProvider } from '../../components/route-provider'
import {
  Serverless,
  SERVERLESS_MODULES
} from '../../components/serverless/serverless'
import { ServersGrid, ServersGridProvider } from '../../components/servers-grid'
import {
  ApplicationVulnerabilitiesGrid,
  ApplicationVulnerabilitiesGridContextProvider,
  ApplicationVulnerabiltiesPagingNav,
  OrganizationVulnerabilitiesGrid,
  OrganizationVulnerabilitiesGridContextProvider,
  OrganizationVulnerabiltiesPagingNav,
  ServerVulnerabilitiesGrid,
  ServerVulnerabilitiesGridContextProvider,
  ServerVulnerabiltiesPagingNav
} from '../../components/vulnerabilities-grid'
import { compose } from '../../util'
import { wrapWithApolloProvider } from '../apollo-wrapper'
import { attackEventsGridBridge } from '../components/attack-events-grid/attack-events-grid'
import { wrapWithErrorBoundary } from '../react-bridge/error-boundary'
import { wrapWithI18nBridge } from '../react-bridge/i18n-bridge'
import { wrapWithI18nProvider } from '../react-bridge/i18n-provider'
import { wrapWithReactQuery } from '../react-query-wrapper'
import { wrapWithSplitClient } from '../splitio-wrapper'
import {
  getProviderWrapper,
  getProviderWrapperWithRootScope
} from './custom-context-wrapper'
import { LibrariesPageProvider } from '../../sca/libraries/pages'
import { SCMOnboardingPageRemoteComponent } from '../../sca/scm-onboarding/scm-onboarding-page-remote-component'
import { wrapWithSkeletorLocalizationProvider } from '../react-bridge/skeletor-localization-provider'
import { wrapWithSkeletorThemeProvider } from '../react-bridge/skeletor-theme-provider'
import { SCMOptionsPage } from '../../sca/scm-options/scm-options-page'
import { SCMConfigurationPage } from '../../sca/sca-configuration'
import { RemoteRemediationSummary } from '../../components/reports/remediation-summary'
import { ReportDashboard } from '../../components/report-dashboard'
import { ActiveGridProvider } from '../../sca/libraries/components/filters/active-grid-provider'

/**
 * This is a small framework for sharing state between React components
 * that exist in different parts of the DOM, but inside of an Angular module.
 *
 * Although it is based on ngReact, it is very slimmed-down and intended to only
 * be used by React components which require state to be managed inside of the React
 * component tree. To that end, there is intentionally no way to configure
 * directives generated this way to pass data to or from an Angular scope.
 *
 * There are two different types of custom components this supports:
 *
 *   1. Components that will be rendered at the root and will remain mounted
 *      regardless of any route change. These components are intended to manage
 *      state using the React Context API. These components should not render
 *      any HTML elements.
 *
 *   2. Components that require some state from React Context and need to live
 *      in a specific place in the DOM. These components will be declared in
 *      Angular templates using a unique Angular directive.
 *
 */

const componentCache: {
  [id: string]: {
    readonly Component: React.ComponentType
    readonly element: HTMLElement
  }
} = {}

let Root
let rootEl
let rootRenderer

const directiveNames: string[] = []

export function generateAngularDirective(
  directiveName: string,
  Component: React.ComponentType
) {
  if (directiveNames.indexOf(directiveName) > -1) {
    throw new Error(
      `Angular Directive with name ${directiveName} already exists!`
    )
  }
  directiveNames.push(directiveName)

  return angular
    .module(`contrast.${directiveName}.module`, [])
    .directive(directiveName, () => {
      let directiveCounter = 0
      return {
        restrict: 'E',
        replace: true,
        link: (scope, element) => {
          const directiveID = directiveName + directiveCounter++

          componentCache[directiveID] = {
            Component: wrapWithErrorBoundary(Component),
            element: element[0]
          }

          if (rootRenderer) {
            rootRenderer.render(<Root />)
          }

          Performance.mark(`react.render.${directiveName}`)

          scope.$on('$destroy', () => {
            delete componentCache[directiveID]
            rootRenderer.render(<Root />)
          })
        }
      }
    }).name
}

export const sharedReactStateModuleName = angular
  .module('contrast.shared-react-state.module', [
    generateAngularDirective(
      'reactOrganizationVulnerabilitiesGrid',
      OrganizationVulnerabilitiesGrid
    ),
    generateAngularDirective('reactAppPolicyTable', AppPolicyTable),
    generateAngularDirective(
      'reactApplicationVulnerabilitiesGrid',
      ApplicationVulnerabilitiesGrid
    ),
    generateAngularDirective(
      'reactMfeIntegrationsPanel',
      IntegrationsPanelComponent
    ),
    generateAngularDirective(
      'reactServerVulnerabilitiesGrid',
      ServerVulnerabilitiesGrid
    ),
    generateAngularDirective('reactOrganizationLibrariesGrid', () => {
      return (
        <ActiveGridProvider>
          <LibrariesPageProvider gridType="Organization" />
        </ActiveGridProvider>
      )
    }),
    generateAngularDirective('reactApplicationLibrariesGrid', () => {
      return (
        <ActiveGridProvider>
          <LibrariesPageProvider gridType="Application" />
        </ActiveGridProvider>
      )
    }),

    generateAngularDirective('reactServerLibrariesGrid', () => {
      return (
        <ActiveGridProvider>
          <LibrariesPageProvider gridType="Server" />
        </ActiveGridProvider>
      )
    }),
    generateAngularDirective(
      'reactOrganizationVulnerabilitiesPagingNav',
      OrganizationVulnerabiltiesPagingNav
    ),
    generateAngularDirective(
      'reactApplicationVulnerabilitiesPagingNav',
      ApplicationVulnerabiltiesPagingNav
    ),
    generateAngularDirective(
      'reactServerVulnerabilitiesPagingNav',
      ServerVulnerabiltiesPagingNav
    ),
    generateAngularDirective('reactServersGrid', ServersGrid),
    generateAngularDirective('reactApplicationsGrid', ApplicationsGrid),
    generateAngularDirective('reactApplicationObserveTab', AppObserveTab),
    generateAngularDirective('reactAppConfigPage', AppConfigPage),
    generateAngularDirective('reactServerless', () => {
      return <Serverless module={SERVERLESS_MODULES.ENTRY} />
    }),
    generateAngularDirective('reactContainers', () => {
      return <Serverless module={SERVERLESS_MODULES.CONTAINERS_ENTRY} />
    }),
    generateAngularDirective(
      'reactApplicationRouteCoverageGrid',
      RouteCoverageGrid
    ),
    attackEventsGridBridge,
    generateAngularDirective('reactRepositoriesGrid', RepositoriesGrid),
    generateAngularDirective(
      'reactScmOnboarding',
      SCMOnboardingPageRemoteComponent
    ),
    generateAngularDirective('reactScmOptions', SCMOptionsPage),
    generateAngularDirective('reactScmConfiguration', SCMConfigurationPage),
    generateAngularDirective(
      'reactRemediationSummary',
      RemoteRemediationSummary
    ),
    generateAngularDirective('reactReportDashboard', ReportDashboard),
    generateAngularDirective('reactAttackEventsPage', AttackEventsPage)
  ])
  .run([
    '$rootScope',
    '$translate',
    (rootScope, translate) => {
      if (!rootEl) {
        rootEl = document.createElement('div')
        document.body.appendChild(rootEl)

        const contextWrapper = compose(
          (WrappedComponent) => wrapWithReactQuery(WrappedComponent, rootScope),
          (WrappedComponent) =>
            wrapWithSplitClient(WrappedComponent, rootScope),
          wrapWithApolloProvider(rootScope),
          wrapWithI18nBridge,
          wrapWithI18nProvider,
          wrapWithSkeletorLocalizationProvider,
          wrapWithSkeletorThemeProvider,
          getProviderWrapper(RouteProvider),
          getProviderWrapper(ServersGridProvider),
          getProviderWrapper(ApplicationsGridProvider),
          getProviderWrapper(OrganizationVulnerabilitiesGridContextProvider),
          getProviderWrapperWithRootScope(
            ApplicationVulnerabilitiesGridContextProvider
          ),
          getProviderWrapper(ServerVulnerabilitiesGridContextProvider),

          getProviderWrapper(RouteCoverageGridProvider),
          getProviderWrapper(AttackEventsGridProvider)
        )
        const RootWithI18n = contextWrapper(() => {
          return (
            <React.Fragment>
              {Object.keys(componentCache).map((k) => {
                const { Component, element } = componentCache[k]
                return ReactDOM.createPortal(<Component />, element)
              })}
            </React.Fragment>
          )
        })
        Root = () => (
          <RootWithI18n
            ngTranslate={{ rootScope, translate }}
            rootScope={rootScope}
          />
        )
        Root.displayName = 'ReactSharedStateRoot'
        rootRenderer = createRoot(rootEl)
        rootRenderer.render(<Root />)
      }
    }
  ]).name
